import { useState } from 'react';
import pluralize from 'pluralize';

import { formatPrice } from 'common/formatters';
import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  LISTING_CMA_DISCUSSION_ACKNOWLEDGED,
  LISTING_CMA_DISCUSSION_AGREED,
  LISTING_CMA_DISCUSSION_NOT_DISCUSSED,
  LISTING_CMA_PRICE_PERCENTAGE,
} from 'common/listingConstants';

import useDispatch from 'client/hooks/useDispatch';
import { SR_MOBILE_APP_ACTION_TYPE_OPEN_URL } from 'client/helpers/sendDataToMobileApp';
import { actionDataTableLoadItems } from 'client/helpers/dataTables';
import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';

export const SR_PRICE_COMPARISON_CARD_COLOR_GREEN = 'SR_PRICE_COMPARISON_CARD_COLOR_GREEN';
export const SR_PRICE_COMPARISON_CARD_COLOR_ORANGE = 'SR_PRICE_COMPARISON_CARD_COLOR_ORANGE';
const LISTING_CMA_PRICE_IS_IN_RANGE = 'LISTING_PRICE_IS_IN_RANGE';

const SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING = {
  [SR_PRICE_COMPARISON_CARD_COLOR_GREEN]: 'colorTypeGreen',
  [SR_PRICE_COMPARISON_CARD_COLOR_ORANGE]: 'colorTypeOrange',
};

const SR_PRICE_DISCUSSION_HEADER = {
  [LISTING_CMA_DISCUSSION_NOT_DISCUSSED]: {
    text: 'Pricing has not been discussed with partner',
    color: 'red',
    icon: '/images/x-circle-icon-red.svg',
  },
  [LISTING_CMA_DISCUSSION_ACKNOWLEDGED]: {
    text: 'The partner acknowledged it’s overpriced and not willing to lower price',
    color: 'orange300',
    icon: '/images/checkmark-circle-orange.svg',
  },
  [LISTING_CMA_DISCUSSION_AGREED]: {
    text: 'The partner has agreed to lower the price',
    color: 'green',
    icon: '/images/checkmark-circle-green-no-shadow.svg',
  },
  [LISTING_CMA_PRICE_IS_IN_RANGE]: {
    text: 'Price is already near or matching the CMA price',
    color: 'green',
    icon: '/images/checkmark-circle-green-no-shadow.svg',
  },
};

const useSrPriceComparisonCardController = ({
  listing,
  userAgent,
  appUrl,
  sendDataToMobileApp,
  openLowerPriceModal,
}) => {
  const [isDiscussionModalOpen, setIsDiscussionModalOpen] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const isMobileApp = isUserAgentMobileApp(userAgent);
  const dispatch = useDispatch();

  const _isRentPriceTooHigh = () => {
    if (!listing.comparativeMarketAnalysisRentPrice) {
      return false;
    }
    return (
      listing.price > listing.comparativeMarketAnalysisRentPrice * LISTING_CMA_PRICE_PERCENTAGE
    );
  };

  const _onViewReportClick = () => {
    if (isMobileApp) {
      sendDataToMobileApp({
        actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
        url: `${appUrl}/pdf-viewer?pdfUrl=${encodeURIComponent(
          listing.comparativeMarketAnalysisPdfUrl,
        )}`,
      });
    } else {
      global.open(
        `${appUrl}/pdf-viewer?pdfUrl=${encodeURIComponent(
          listing.comparativeMarketAnalysisPdfUrl,
        )}`,
        '_blank',
      );
    }
  };

  const _getNotesTitle = () => {
    return listing.comparativeMarketAnalysisNotes ? 'Notes from Sunroom Leasing' : null;
  };

  const _getLowerPriceButtonText = () => {
    return _isRentPriceTooHigh()
      ? `Lower the Price to ${formatPrice(listing.comparativeMarketAnalysisRentPrice)}`
      : null;
  };

  const _getDiscussionNotes = () => {
    if (!listing.comparativeMarketAnalysisDiscussionNotes) {
      return '';
    }
    return (
      `${listing.comparativeMarketAnalysisDiscussionNotes} ` +
      `- ${listing.comparativeMarketAnalysisDiscussedByUser}, ` +
      `${momentWithAustinTimezone(listing.comparativeMarketAnalysisDiscussedAt).format(
        'MMM DD HH:mm:ss',
      )}`
    );
  };

  const _getDiscussionHeader = () => {
    const isRentPriceTooHigh = _isRentPriceTooHigh();
    if (isRentPriceTooHigh) {
      switch (listing.comparativeMarketAnalysisDiscussed) {
        case LISTING_CMA_DISCUSSION_NOT_DISCUSSED:
          return SR_PRICE_DISCUSSION_HEADER[LISTING_CMA_DISCUSSION_NOT_DISCUSSED];
        case LISTING_CMA_DISCUSSION_ACKNOWLEDGED:
          return SR_PRICE_DISCUSSION_HEADER[LISTING_CMA_DISCUSSION_ACKNOWLEDGED];
        case LISTING_CMA_DISCUSSION_AGREED:
          return SR_PRICE_DISCUSSION_HEADER[LISTING_CMA_DISCUSSION_AGREED];
        default:
          return null;
      }
    }
    return SR_PRICE_DISCUSSION_HEADER[LISTING_CMA_PRICE_IS_IN_RANGE];
  };

  const _onZillowRedirect = () => {
    global.open(listing.comparativeMarketAnalysisNearbyZillowListingsUrl, '_blank');
  };

  const reloadDataTableListings = () => {
    dispatch(actionDataTableLoadItems, {
      itemsPropName: 'listings',
      dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
      viewId: `by-listingId-${listing.id}`,
      count: 1,
      filter: {
        'listings.id': listing.id,
      },
    });
  };

  return {
    isRentPriceTooHigh: _isRentPriceTooHigh(),
    pricing: {
      listing: listing.price,
      color: _isRentPriceTooHigh()
        ? SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING[SR_PRICE_COMPARISON_CARD_COLOR_ORANGE]
        : SR_PRICE_COMPARISON_CARD_COLOR_CLASSNAME_MAPPING[SR_PRICE_COMPARISON_CARD_COLOR_GREEN],
      listingLabel: 'Your rent price',
      suggested: listing.comparativeMarketAnalysisRentPrice,
      suggestedLabel: 'CMA suggested rate',
      overRateLabel: 'Over suggested rate',
      overRateDiff: listing.price - listing.comparativeMarketAnalysisRentPrice,
      currentPriceText: `Currently Priced at ${formatPrice(listing.price)}`,
      isTooHigh: _isRentPriceTooHigh(),
    },
    notes: {
      title: _getNotesTitle(),
      text: listing.comparativeMarketAnalysisNotes,
      hasNotes: !!listing.comparativeMarketAnalysisNotes,
      shouldShowNotes:
        !!listing.comparativeMarketAnalysisNotes &&
        listing.comparativeMarketAnalysisDiscussed === LISTING_CMA_DISCUSSION_ACKNOWLEDGED &&
        _isRentPriceTooHigh(),
    },
    actions: {
      viewReport: {
        label:
          'View Report ' +
          `(created ${momentWithAustinTimezone(listing.comparativeMarketAnalysisUpdated).format(
            'MMM DD, YYYY',
          )})`,
        onClick: _onViewReportClick,
      },
      lowerPrice: {
        label: _getLowerPriceButtonText(),
        onClick: openLowerPriceModal,
      },
    },
    cmaDiscussionModal: {
      isOpen: isDiscussionModalOpen,
      onOpen: () => setIsDiscussionModalOpen(true),
      onClose: () => setIsDiscussionModalOpen(false),
      onSuccess: reloadDataTableListings,
    },
    zillowMap: {
      isMapExpanded,
      onZillowRedirect: _onZillowRedirect,
      onExpandMap: () => setIsMapExpanded(true),
      onClose: () => setIsMapExpanded(false),
    },
    cma: {
      discussion: {
        hasBeenDiscussed: listing.comparativeMarketAnalysisDiscussed,
        notes: _getDiscussionNotes(),
        header: _getDiscussionHeader(),
      },
      nearbyZillowListings: {
        hasInformation:
          !!listing.comparativeMarketAnalysisNearbyZillowListingsCount &&
          !!listing.comparativeMarketAnalysisNearbyZillowListingsScreenshotUrl &&
          !!listing.comparativeMarketAnalysisNearbyZillowListingsUrl,
        count: listing.comparativeMarketAnalysisNearbyZillowListingsCount,
        screenshotUrl: listing.comparativeMarketAnalysisNearbyZillowListingsScreenshotUrl,
        zillowUrl: listing.comparativeMarketAnalysisNearbyZillowListingsUrl,
        title:
          `${listing.comparativeMarketAnalysisNearbyZillowListingsCount} Nearby similar ` +
          `${pluralize('Listing', listing.comparativeMarketAnalysisNearbyZillowListingsCount)} ` +
          'on Zillow',
      },
    },
  };
};

export default useSrPriceComparisonCardController;
